import { useEffect, useState } from "react";
import { createContext } from "react";
import store from "../store";
import { useSyncedStore } from "@syncedstore/react";
import { nanoid } from "nanoid";
import { scene } from "../three";

const UIContext = createContext();

export function UIProvider({ children }) {
  const edges = useSyncedStore(store.edges);
  const nodes = useSyncedStore(store.nodes);
  const controlValues = useSyncedStore(store.controlValues);

  const [mousePos, setMousePos] = useState({ x: 150, y: 70 });
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [sceneLoaded, setSceneLoaded] = useState(false);
  const [selectedInput, setSelectedInput] = useState(null);
  const [selectedOutput, setSelectedOutput] = useState(null);

  useEffect(() => {
    document.addEventListener("click", (e) => {
      if (e.target.tagName === "CANVAS") {
        setSelectedInput(null);
        setSelectedOutput(null);
      }
    });
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        setSelectedInput(null);
        setSelectedOutput(null);
      }
    });
  }, []);

  function selectInput(id) {
    if (selectedOutput) {
      const alreadyHasConnection = edges.find(
        (edge) => edge.source === selectedOutput && edge.target === id
      );
      if (!alreadyHasConnection) {
        edges.push({ id: nanoid(), source: selectedOutput, target: id });
      }
      setSelectedOutput(null);
    } else {
      setSelectedInput(id);
    }
  }

  function selectOutput(id) {
    if (selectedInput) {
      const alreadyHasConnection = edges.find(
        (edge) => edge.source === id && edge.target === selectedInput
      );
      if (!alreadyHasConnection) {
        edges.push({ id: nanoid(), source: id, target: selectedInput });
      }
      setSelectedInput(null);
    } else {
      setSelectedOutput(id);
    }
  }

  function removeOutput(id) {
    edges.forEach((edge) => {
      const canRemove = edge.source === id;
      if (canRemove) {
        const index = edges.findIndex((e) => e.id === edge.id);
        edges.splice(index, 1);
      }
    });
  }

  function removeInput(id) {
    edges.forEach((edge) => {
      const canRemove = edge.target === id;
      if (canRemove) {
        const index = edges.findIndex((e) => e.id === edge.id);
        edges.splice(index, 1);
      }
    });
  }

  function removeNode(id) {
    edges.forEach((edge) => {
      const canRemove =
        edge.target.split(":")[0] === id || edge.source.split(":")[0] === id;
      if (canRemove) {
        const index = edges.findIndex((e) => e.id === edge.id);
        edges.splice(index, 1);
      }
    });

    // delete node itself
    const index = nodes.findIndex((n) => n.id === id);
    nodes.splice(index, 1);
  }

  function addNode(name) {
    const id = nanoid();

    const node = {
      id,
      name: name,
      visible: true,
      position: { x: mousePos.x - 30, y: mousePos.y - 30 },
      component: name,
    };

    nodes.push(node);
  }

  return (
    <UIContext.Provider
      value={{
        state: {
          mousePos,
          sceneLoaded,
          controlValues,
          selectedInput,
          selectedOutput,
          showSearchInput,
        },
        methods: {
          setMousePos,
          setShowSearchInput,
          setSceneLoaded,
          selectInput,
          selectOutput,
          removeInput,
          removeOutput,
          removeNode,
          addNode,
        },
      }}
    >
      {children}
    </UIContext.Provider>
  );
}

export default UIContext;
